import {
  useMemo,
} from 'react';
import type {
  ReactElement,
  ReactNode,
} from 'react';

import { Link } from 'react-router-dom';

import styled, { useTheme } from 'styled-components';

export type HomeBreadcrumbType = {
  url: string;
  type: 'HOME';
};

export type BreadcrumbType = {
  url?: string;
  title: string;
};

export const checkIsHome = (
  breadcrumb: HomeBreadcrumbType | BreadcrumbType,
): breadcrumb is HomeBreadcrumbType => (breadcrumb as HomeBreadcrumbType).type === 'HOME';

export type BreadcrumbsProps = {
  /**
   * Ссылка на корень
   */
  homeLink?: string;
  /**
   * массив объектов хлебных крошек
   */
  breadcrumbs: BreadcrumbType[];
};

export const StyledBreadcrumbs = styled.div(({
  theme: {
    breadcrumbs,
    fonts,
  },
}) => ({
  ...fonts[breadcrumbs.FONT],
  marginTop: breadcrumbs.VERTICAL_OFFSET,
  marginBottom: breadcrumbs.VERTICAL_OFFSET,
}));

export const StyledHomeLink = styled(Link)(({
  theme: {
    breadcrumbs,
    colors,
  },
}) => ({
  textDecoration: 'none',

  '&, &:visited': {
    color: colors[breadcrumbs.ITEM_COLOR],
  },

  '&:hover': {
    color: colors[breadcrumbs.ITEM_COLOR_HOVER],
  },

  '&:active': {
    color: colors[breadcrumbs.ITEM_COLOR_PRESSED],
  },
}));

export const StyledLink = styled(Link)(({
  theme: {
    breadcrumbs,
    colors,
  },
}) => ({
  textDecoration: 'none',

  '&, &:visited': {
    color: colors[breadcrumbs.ITEM_COLOR],
  },

  '&:hover': {
    color: colors[breadcrumbs.ITEM_COLOR_HOVER],
  },

  '&:active': {
    color: colors[breadcrumbs.ITEM_COLOR_PRESSED],
  },
}));

export const StyledText = styled.span(({
  theme: {
    breadcrumbs,
    colors,
  },
}) => ({
  color: colors[breadcrumbs.LAST_ITEM_COLOR],
}));

export const StyledSeparator = styled.span(({
  theme: {
    breadcrumbs,
    colors,
  },
}) => ({
  color: colors[breadcrumbs.SEPARATOR_COLOR],
  marginLeft: breadcrumbs.SEPARATOR_OFFSET,
  marginRight: breadcrumbs.SEPARATOR_OFFSET,
}));

export function Breadcrumbs({
  homeLink = undefined,
  breadcrumbs: breadcrumbsProp,
}: BreadcrumbsProps): ReactElement {
  const {
    breadcrumbs: breadcrumbsTheme,
  } = useTheme();

  const breadcrumbs = useMemo<Array<BreadcrumbType | HomeBreadcrumbType>>(() => {
    if (homeLink) {
      return [
        {
          url: homeLink,
          type: 'HOME',
        },
        ...breadcrumbsProp,
      ];
    }

    return breadcrumbsProp;
  }, [
    breadcrumbsProp,
    homeLink,
  ]);

  const lastItemIndex = breadcrumbs.length - 1;

  return (
    <StyledBreadcrumbs>
      {
        breadcrumbs.reduce<ReactNode[]>((res, breadcrumb, index) => {
          const itemKey = `item_${index}`;

          if (checkIsHome(breadcrumb)) {
            res.push(
              <StyledHomeLink
                to={breadcrumb.url}
                key="@n3/HOME"
              >
                {breadcrumbsTheme.HOME_ICON}
              </StyledHomeLink>,
            );
          } else {
            const {
              url,
              title,
            } = breadcrumb;

            if (url) {
              res.push(
                <StyledLink
                  to={url}
                  key={itemKey}
                >
                  {title}
                </StyledLink>,
              );
            } else {
              res.push(
                <StyledText
                  key={itemKey}
                >
                  {title}
                </StyledText>,
              );
            }
          }

          if (index < lastItemIndex) {
            res.push(
              <StyledSeparator
                key={`divider_${index}`}
              >
                {breadcrumbsTheme.SEPARATOR}
              </StyledSeparator>,
            );
          }

          return res;
        }, [])
      }
    </StyledBreadcrumbs>
  );
}
